// SPDX-License-Identifier: BUSL-1.1

/**
 * Badge components for status display.
 *
 */

import type { PiDevAgentStatus, PiDevSessionStatus, PolicyGate } from '@/types/api';
import {
  AGENT_HEALTH_LABELS,
  type AgentHealth,
  type AgentHealthTone,
  AGENT_HEALTH_TONE,
  deriveAgentHealth,
  type DeriveAgentHealthInput,
} from '@/lib/agent-health';
import {
  HEARTBEAT_INVOCATION_SOURCE_LABELS,
  type HeartbeatInvocationSource,
} from '@cli-ceo/core';

/* ── Agent Status Badges ─────────────────────────── */

export function PiDevAgentStatusBadge({ status }: { status: PiDevAgentStatus }) {
  const colors: Record<PiDevAgentStatus, string> = {
    idle: 'bg-yellow-900/40 text-yellow-400',
    running: 'bg-green-900/30 text-green-400',
    paused: 'bg-blue-900/30 text-blue-400',
    terminated: 'bg-neutral-700 text-neutral-300',
  };

  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-xs ${colors[status]}`}>
      {status}
    </span>
  );
}

export function PiDevSessionStatusBadge({ status }: { status: PiDevSessionStatus }) {
  const colors: Record<PiDevSessionStatus, string> = {
    active: 'bg-green-900/30 text-green-400',
    completed: 'bg-neutral-700 text-neutral-300',
    terminated: 'bg-red-900/30 text-red-400',
  };

  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-xs ${colors[status]}`}>
      {status}
    </span>
  );
}

/* ── Policy Gate Badge ──────────────────────────── */

export function PolicyGateBadge({ gate }: { gate: PolicyGate }) {
  const colors: Record<PolicyGate, string> = {
    safe: 'bg-green-900/30 text-green-400',
    guarded: 'bg-yellow-900/40 text-yellow-400',
    manual: 'bg-red-900/30 text-red-400',
  };

  const labels: Record<PolicyGate, string> = {
    safe: 'Safe',
    guarded: 'Guarded',
    manual: 'Manual',
  };

  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-xs ${colors[gate]}`}>
      {labels[gate]}
    </span>
  );
}

/* ── Budget Usage Bar ────────────────────────────── */

export function BudgetUsageBar({ 
  used, 
  limit, 
  percentUsed 
}: { 
  used: number; 
  limit: number; 
  percentUsed: number | null;
}) {
  if (!limit) {
    return <span className="text-neutral-500">Unlimited</span>;
  }

  const percent = percentUsed ?? (limit > 0 ? (used / limit) * 100 : 0);
  const color = percent > 90 ? 'bg-red-500' : percent > 75 ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-20 overflow-hidden rounded-full bg-neutral-800">
        <div 
          className={`h-full ${color}`}
          style={{ width: `${Math.min(100, percent)}%` }}
        />
      </div>
      <span className="text-xs text-neutral-400">
        {percent.toFixed(0)}%
      </span>
    </div>
  );
}

/* ── Agent Health Badge (HB-02) ─────────────────── */

const AGENT_HEALTH_TONE_CLASSES: Record<AgentHealthTone, string> = {
  ok: 'bg-green-900/30 text-green-400',
  neutral: 'bg-blue-900/30 text-blue-400',
  warning: 'bg-amber-900/30 text-amber-400',
  muted: 'bg-neutral-700 text-neutral-300',
  error: 'bg-red-900/30 text-red-400',
};

export function AgentHealthBadge({ health }: { health: AgentHealth }) {
  const className = AGENT_HEALTH_TONE_CLASSES[AGENT_HEALTH_TONE[health]];
  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-xs ${className}`}>
      {AGENT_HEALTH_LABELS[health]}
    </span>
  );
}

/**
 * Convenience wrapper that derives the health label from an agent's
 * (status, lastHeartbeat) and renders the appropriate badge. Use this
 * when you have the raw agent shape; use AgentHealthBadge directly when
 * the health was already computed upstream.
 */
export function DerivedAgentHealthBadge({ agent }: { agent: DeriveAgentHealthInput }) {
  return <AgentHealthBadge health={deriveAgentHealth(agent)} />;
}

/* ── Heartbeat Invocation Source Badge (HB-07) ───────── */

const HEARTBEAT_SOURCE_CLASSES: Record<HeartbeatInvocationSource, string> = {
  timer: 'bg-blue-900/30 text-blue-300',
  assignment: 'bg-purple-900/30 text-purple-300',
  on_demand: 'bg-amber-900/30 text-amber-300',
  automation: 'bg-cyan-900/30 text-cyan-300',
  unknown: 'bg-neutral-700 text-neutral-400',
};

export function HeartbeatSourceBadge({
  source,
}: {
  source: HeartbeatInvocationSource;
}) {
  return (
    <span
      className={`inline-flex rounded px-2 py-0.5 text-xs font-mono ${HEARTBEAT_SOURCE_CLASSES[source]}`}
    >
      {HEARTBEAT_INVOCATION_SOURCE_LABELS[source]}
    </span>
  );
}
